<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">
                Admin: Projects: Environments: Webhooks
            </h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <!-- Header with tabs -->
                <div class="mb-6">
                    <environment-header-tabs
                        current="webhooks"
                        :project="project.name"
                        :project-key="project.key"
                        :environment="environment.name"
                        :environment-key="environment.key"
                    />
                </div>

                <!-- Webhooks section -->
                <div
                    class="rounded-xl border border-taupe-300 bg-white overflow-hidden p-6 sm:p-7 mb-6"
                >
                    <div
                        class="-mx-6 sm:-mx-7 -mt-6 sm:-mt-7 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-0 bg-taupe-100 border-b border-taupe-300 px-6 sm:px-7 py-4"
                    >
                        <div class="min-w-0">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Webhooks
                            </h2>
                            <p class="mt-1 text-sm font-normal text-taupe-500">
                                Send event notifications from this environment
                                to external endpoints. Create and manage
                                webhooks from one place.
                            </p>
                        </div>
                        <div class="sm:flex-none">
                            <zen-button
                                v-if="
                                    can(
                                        PERMISSIONS.WEBHOOK_CREATE,
                                        permissionScope
                                    )
                                "
                                type="button"
                                @click="openCreateModal()"
                                variant="primary"
                            >
                                Create webhook
                            </zen-button>
                        </div>
                    </div>

                    <!-- Webhooks table -->
                    <div>
                        <div v-if="loading" class="text-sm text-taupe-500 py-4">
                            Loading webhooks…
                        </div>
                        <div
                            v-else-if="!hasResults"
                            class="rounded-lg border border-dashed border-taupe-400 bg-taupe-100"
                        >
                            <zen-empty-table-message
                                compact
                                icon="ZenIconWebhook"
                            >
                                There are no webhooks.
                            </zen-empty-table-message>
                        </div>
                        <div v-else class="overflow-x-auto">
                            <table class="min-w-full divide-y divide-taupe-300">
                                <thead>
                                    <tr>
                                        <th
                                            class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                                        >
                                            URL
                                        </th>
                                        <th
                                            class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                                        >
                                            Events
                                        </th>
                                        <th class="py-3"></th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-taupe-100">
                                    <tr
                                        v-for="result in results"
                                        :key="result.ulid"
                                        class="hover:bg-taupe-50"
                                    >
                                        <td class="py-3 text-sm">
                                            <span
                                                class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium mr-2 border border-sky-500 text-sky-600"
                                            >
                                                {{ result.method }}
                                            </span>
                                            <span
                                                class="font-medium text-taupe-800 break-all"
                                                >{{ result.url }}</span
                                            >
                                        </td>
                                        <td class="py-3">
                                            <div class="flex flex-wrap gap-1">
                                                <span
                                                    v-if="
                                                        isAllEvents(
                                                            result.events
                                                        )
                                                    "
                                                    class="inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-700/10"
                                                    >All</span
                                                >
                                                <span
                                                    v-else
                                                    v-for="event in result.events"
                                                    :key="event"
                                                    class="inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-700/10"
                                                >
                                                    {{ event }}
                                                </span>
                                            </div>
                                        </td>
                                        <td class="py-3 text-right">
                                            <div
                                                class="flex items-center justify-end gap-3"
                                            >
                                                <zen-button
                                                    v-if="
                                                        can(
                                                            PERMISSIONS.WEBHOOK_READ,
                                                            permissionScope
                                                        )
                                                    "
                                                    type="button"
                                                    @click="
                                                        openDeliveriesModal(
                                                            result
                                                        )
                                                    "
                                                    aria-label="Webhook deliveries"
                                                    variant="link-muted"
                                                >
                                                    Deliveries
                                                </zen-button>
                                                <zen-button
                                                    v-if="
                                                        can(
                                                            PERMISSIONS.WEBHOOK_UPDATE,
                                                            permissionScope
                                                        )
                                                    "
                                                    type="button"
                                                    @click="
                                                        openUpdateModal(result)
                                                    "
                                                    aria-label="Edit webhook"
                                                    variant="link"
                                                >
                                                    Edit
                                                </zen-button>
                                                <zen-button
                                                    v-if="
                                                        can(
                                                            PERMISSIONS.WEBHOOK_DELETE,
                                                            permissionScope
                                                        )
                                                    "
                                                    type="button"
                                                    @click="
                                                        openRemoveModal(
                                                            result.ulid
                                                        )
                                                    "
                                                    aria-label="Remove webhook"
                                                    variant="link-danger"
                                                >
                                                    Remove
                                                </zen-button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <remove-webhook-modal
            :is-open="modals.remove.show"
            :processing="modals.remove.processing"
            @close="closeRemoveModal"
            @confirm="confirmRemove()"
        />

        <create-webhook-slide-in
            :is-open="modals.create.show"
            v-model:data="modals.create.data"
            :processing="modals.create.processing"
            :available-events="availableEvents"
            @close="closeCreateModal()"
            @confirm="confirmCreate()"
        />

        <update-webhook-slide-in
            :is-open="modals.update.show"
            v-model:data="modals.update.data"
            :processing="modals.update.processing"
            :available-events="availableEvents"
            @close="closeUpdateModal()"
            @confirm="confirmUpdate()"
        />

        <webhook-created-modal
            :is-open="modals.secret.show"
            :secret="modals.secret.value"
            :copied="modals.secret.copied"
            @close="dismissSecretModal"
            @copy="copySecret"
        />

        <webhook-deliveries-modal
            :is-open="modals.deliveries.show"
            :webhook="modals.deliveries.webhook"
            :loading="modals.deliveries.loading"
            :items="modals.deliveries.items"
            :format-date-time="formatDateTime"
            @close="closeDeliveriesModal"
        />
    </main>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import EnvironmentHeaderTabs from '@/screens/_components/admin/EnvironmentHeaderTabs.vue'
import RemoveWebhookModal from '@/screens/trusted/admin/modals/RemoveWebhookModal.vue'
import CreateWebhookSlideIn from '@/screens/trusted/admin/modals/CreateWebhookSlideIn.vue'
import UpdateWebhookSlideIn from '@/screens/trusted/admin/modals/UpdateWebhookSlideIn.vue'
import WebhookCreatedModal from '@/screens/trusted/admin/modals/WebhookCreatedModal.vue'
import WebhookDeliveriesModal from '@/screens/trusted/admin/modals/WebhookDeliveriesModal.vue'
import EnvironmentWebhookService from '@/services/environment-webhook.service'
import { errorMessage } from '@/utils/notifications.js'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

// Router and Store
const route = useRoute()
const sessionStore = useSessionStore()
const { can } = usePermissions()

// The resource.verb actions an environment webhook may subscribe to.
// An empty `events` list (eventAll = true) subscribes to all of them.
const availableEvents = [
    {
        value: 'target.created',
        label: 'target.created',
        description:
            '— a target is created (e.g. cloned into this environment)',
    },
    {
        value: 'target_rule.created',
        label: 'target_rule.created',
        description: '— a targeting rule is created',
    },
    {
        value: 'target_rule.updated',
        label: 'target_rule.updated',
        description: '— a targeting rule is updated',
    },
    {
        value: 'target_rule.deleted',
        label: 'target_rule.deleted',
        description: '— a targeting rule is deleted',
    },
]

// State
const loading = ref(false)
const results = ref(null)

const modals = reactive({
    create: {
        show: false,
        processing: false,
        data: {
            url: null,
            method: 'GET',
            eventAll: true,
            events: [],
        },
    },
    remove: {
        show: false,
        processing: false,
        ulid: null,
    },
    update: {
        show: false,
        processing: false,
        data: {
            ulid: null,
            url: null,
            method: null,
            eventAll: true,
            events: [],
        },
    },
    secret: {
        show: false,
        value: null,
        copied: false,
    },
    deliveries: {
        show: false,
        loading: false,
        webhook: null,
        items: [],
    },
})

// Computed
const project = computed(() => {
    return sessionStore.data?.projects?.data?.filter(
        (project) => project.key === route.params.projectKey
    )[0]
})

const environment = computed(() => {
    return project.value?.environments?.data?.filter(
        (environment) => environment.key === route.params.environmentKey
    )[0]
})

const hasResults = computed(() => {
    return results.value != null && results.value.length > 0
})

const permissionScope = computed(() => ({
    projectUlid: project.value?.ulid,
    environmentUlid: environment.value?.ulid,
}))

// Methods
const isAllEvents = (events) => {
    return !events || events.length === 0
}

// The one-time secret lives only in the reveal modal, so a list row never
// keeps raw_secret or shown_once, whichever response it came from.
const toListItem = (webhook) => {
    const item = { ...webhook }
    delete item.raw_secret
    delete item.shown_once
    return item
}

const formatDateTime = (iso) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const refresh = async () => {
    loading.value = true
    try {
        const data = await EnvironmentWebhookService.list(
            route.params.projectKey,
            route.params.environmentKey
        )
        results.value = data.data
    } catch (error) {
        errorMessage(error)
    } finally {
        loading.value = false
    }
}

const openCreateModal = () => {
    modals.create.show = true
}

const closeCreateModal = () => {
    modals.create.show = false
    modals.create.data.url = null
    modals.create.data.method = 'GET'
    modals.create.data.eventAll = true
    modals.create.data.events = []
}

const confirmCreate = async () => {
    modals.create.processing = true
    try {
        const payload = {
            url: modals.create.data.url,
            method: modals.create.data.method,
            events: modals.create.data.eventAll
                ? []
                : modals.create.data.events,
        }
        const data = await EnvironmentWebhookService.create(
            route.params.projectKey,
            route.params.environmentKey,
            payload
        )
        results.value = upsertBy(results.value ?? [], toListItem(data.data))
        closeCreateModal()
        if (data.data?.raw_secret) {
            modals.secret.value = data.data.raw_secret
            modals.secret.copied = false
            modals.secret.show = true
        }
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.create.processing = false
    }
}

const openRemoveModal = (id) => {
    modals.remove.ulid = id
    modals.remove.show = true
}

const closeRemoveModal = () => {
    modals.remove.show = false
    modals.remove.ulid = null
}

const confirmRemove = async () => {
    modals.remove.processing = true
    try {
        await EnvironmentWebhookService.remove(
            route.params.projectKey,
            route.params.environmentKey,
            modals.remove.ulid
        )
        results.value = removeBy(results.value, modals.remove.ulid)
        closeRemoveModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.remove.processing = false
    }
}

const openUpdateModal = (webhook) => {
    const events = webhook.events ?? []
    modals.update.data.ulid = webhook.ulid
    modals.update.data.url = webhook.url
    modals.update.data.method = webhook.method
    modals.update.data.eventAll = events.length === 0
    modals.update.data.events = [...events]
    modals.update.show = true
}

const closeUpdateModal = () => {
    modals.update.show = false
    modals.update.data.ulid = null
    modals.update.data.url = null
    modals.update.data.method = null
    modals.update.data.eventAll = true
    modals.update.data.events = []
}

const confirmUpdate = async () => {
    modals.update.processing = true
    try {
        const payload = {
            url: modals.update.data.url,
            method: modals.update.data.method,
            events: modals.update.data.eventAll
                ? []
                : modals.update.data.events,
        }
        const data = await EnvironmentWebhookService.update(
            route.params.projectKey,
            route.params.environmentKey,
            modals.update.data.ulid,
            payload
        )
        results.value = upsertBy(results.value, toListItem(data.data))
        closeUpdateModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.update.processing = false
    }
}

// -----------------------------------------------------------------------
// Secret reveal
// -----------------------------------------------------------------------
const copySecret = async () => {
    try {
        await navigator.clipboard.writeText(modals.secret.value)
        modals.secret.copied = true
        setTimeout(() => {
            modals.secret.copied = false
        }, 2000)
    } catch {
        errorMessage(
            'Could not copy to clipboard. Please select and copy the secret manually.'
        )
    }
}

const dismissSecretModal = () => {
    modals.secret.show = false
    modals.secret.value = null
    modals.secret.copied = false
}

// -----------------------------------------------------------------------
// Deliveries
// -----------------------------------------------------------------------
const openDeliveriesModal = async (webhook) => {
    modals.deliveries.webhook = webhook
    modals.deliveries.items = []
    modals.deliveries.show = true
    modals.deliveries.loading = true
    try {
        const data = await EnvironmentWebhookService.deliveries(
            route.params.projectKey,
            route.params.environmentKey,
            webhook.ulid
        )
        modals.deliveries.items = data.data ?? []
    } catch (error) {
        errorMessage(error)
        modals.deliveries.show = false
    } finally {
        modals.deliveries.loading = false
    }
}

const closeDeliveriesModal = () => {
    modals.deliveries.show = false
    modals.deliveries.webhook = null
    modals.deliveries.items = []
}

// Lifecycle
onMounted(() => {
    refresh()
})
</script>
